/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useCallback } from 'react';
import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuthStore } from '../store/authStore';

const tourSteps: DriveStep[] = [
  {
    element: '#nav-dashboard',
    popover: {
      title: 'Welcome to Podstack! 👋',
      description: 'This is your dashboard where you can see an overview of your resources, billing, and recent activity.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-projects',
    popover: {
      title: 'Projects',
      description: 'Organize your resources into projects. Each project can have its own storage, pods, and team members.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-pods',
    popover: {
      title: 'GPU Pods',
      description: 'Launch powerful GPU-accelerated containers for ML training, inference, or any compute workload.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-storage',
    popover: {
      title: 'Object Storage',
      description: 'S3-compatible storage for your datasets, models, and files. Upload and manage your data easily.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-volumes',
    popover: {
      title: 'Persistent Volumes',
      description: 'Create NFS volumes that persist across pod restarts. Perfect for checkpoints and shared data.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-templates',
    popover: {
      title: 'Launch Templates',
      description: 'Save and reuse configurations for quick pod deployment. Use public templates or create your own.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-wallet',
    popover: {
      title: 'Wallet & Billing',
      description: 'Manage your balance, top up credits, and view your spending. Switch to postpaid billing anytime.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#nav-kyc',
    popover: {
      title: 'KYC Verification',
      description: 'Complete KYC to unlock postpaid billing and higher limits. It only takes a few minutes.',
      side: 'right',
      align: 'start',
    },
  },
];

export const useTour = () => {
  const { setShowTour } = useAuthStore();

  const startTour = useCallback(() => {
    const driverObj = driver({
      showProgress: true,
      steps: tourSteps,
      nextBtnText: 'Next',
      prevBtnText: 'Previous',
      doneBtnText: 'Get Started!',
      onDestroyStarted: async () => {
        // Save preference when tour is completed or skipped
        await setShowTour(false);
        driverObj.destroy();
      },
    });

    driverObj.drive();
  }, [setShowTour]);

  const skipTour = useCallback(async () => {
    await setShowTour(false);
  }, [setShowTour]);

  return { startTour, skipTour };
};
